import { useEffect, useState, useCallback } from "react";
import { apiFetch } from "../context/AuthContext";

const B     = "#2C1A0E";
const CREAM = "#FAF7F2";

const MONTHS  = ["Apr-26","May-26","Jun-26","Jul-26","Aug-26","Sep-26",
                 "Oct-26","Nov-26","Dec-26","Jan-27","Feb-27","Mar-27"];
const MLABELS = ["Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec","Jan","Feb","Mar"];
const CATS = [
  { key:"revenue",   label:"💚 Revenue",        bg:"#F0F7E9" },
  { key:"cogs",      label:"🟠 Cost of Goods",  bg:"#FEF3EE" },
  { key:"opex",      label:"🔵 Salaries & Ops", bg:"#EEF4FB" },
  { key:"marketing", label:"🟣 Marketing",       bg:"#F5EEF8" },
  { key:"admin",     label:"⬜ Admin & Tools",   bg:"#F7F5F2" },
];

const COL_W   = 190; // frozen col width px
const CELL_W  = 80;  // each plan/actual cell width

export default function Cashflow() {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  // pending plan edits: { rowId: { month: newValue } }
  const [pending, setPending] = useState({});
  const [saving,  setSaving]  = useState(false);

  useEffect(() => {
    apiFetch("/api/financials", {})
      .then(r => r.json()).then(setRows).finally(() => setLoading(false));
  }, []);

  // Track plan cell changes without saving yet
  const stagePlan = (id, val) => {
    setPending(p => ({ ...p, [id]: val }));
  };

  // Save all pending plan changes
  const commitPlan = async () => {
    setSaving(true);
    await Promise.all(
      Object.entries(pending).map(([id, val]) =>
        apiFetch(`/api/financials/${id}`, {
          method: "PATCH",
          body: JSON.stringify({ plan_amount: val === "" ? null : Number(val) }),
        })
      )
    );
    // Apply to local state
    setRows(r => r.map(x => x._id in pending
      ? { ...x, plan_amount: pending[x._id] === "" ? null : Number(pending[x._id]) }
      : x
    ));
    setPending({});
    setSaving(false);
  };

  // Discard pending changes
  const cancelPlan = () => setPending({});

  // Save actual immediately (Zoho actuals — no staging needed)
  const saveActual = async (id, val) => {
    await apiFetch(`/api/financials/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ actual_amount: val === "" ? null : Number(val) }),
    });
    setRows(r => r.map(x => x._id === id
      ? { ...x, actual_amount: val === "" ? null : Number(val) } : x));
  };

  if (loading) return (
    <div style={{ padding: 40, textAlign: "center", color: "#999", fontSize: 16 }}>
      Loading cashflow…
    </div>
  );

  const byLine = {};
  rows.forEach(r => {
    if (!byLine[r.line_item]) byLine[r.line_item] = { info: r, months: {} };
    byLine[r.line_item].months[r.month] = r;
  });

  const nets = MONTHS.map(m => {
    const rev  = rows.filter(r => r.category === "revenue" && r.month === m)
                     .reduce((s, r) => s + (r.actual_amount ?? r.plan_amount ?? 0), 0);
    const cost = rows.filter(r => r.category !== "revenue" && r.month === m)
                     .reduce((s, r) => s + (r.actual_amount ?? r.plan_amount ?? 0), 0);
    return { rev, net: rev - cost };
  });

  const hasPending = Object.keys(pending).length > 0;

  return (
    <div style={{ padding: "28px 28px 48px", fontFamily: "Inter, sans-serif" }}>
      <h1 style={{ fontFamily: "'Cormorant Garamond',Georgia,serif", color: B,
                   fontSize: 26, fontWeight: 700, marginBottom: 8 }}>
        💰 Cashflow — Plan vs Actual
      </h1>

      {/* Info bar */}
      <div style={{ background: "#FFF8E7", border: "1px solid #F0D080", padding: "10px 14px",
                    borderRadius: 8, fontSize: 13, color: "#7A5C00", marginBottom: 16, lineHeight: 1.7 }}>
        ✏️ <strong style={{ color: "#2F5496" }}>Plan</strong> cells are editable.
        Changes are <strong>staged</strong> and only saved when you click <strong>Save Changes</strong>.
        &nbsp;&nbsp;
        📒 <strong>Actual</strong> cells save immediately (from Zoho Books).
      </div>

      {/* Pending changes banner */}
      {hasPending && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16,
                      background: "#EBF1F9", border: "1px solid #A8C4E0",
                      padding: "10px 16px", borderRadius: 8 }}>
          <span style={{ fontSize: 13, color: "#1A4A7A", flex: 1 }}>
            ⚠️ <strong>{Object.keys(pending).length} unsaved plan change{Object.keys(pending).length > 1 ? "s" : ""}</strong> — review before saving.
          </span>
          <button onClick={cancelPlan} style={btnCancel}>Discard</button>
          <button onClick={commitPlan} disabled={saving} style={btnSave}>
            {saving ? "Saving…" : "✓ Save Changes"}
          </button>
        </div>
      )}

      {/* TWO-PANEL layout: frozen left + scrollable right */}
      <div style={{ display: "flex", borderRadius: 12, overflow: "hidden",
                    boxShadow: "0 2px 16px rgba(44,26,14,0.1)",
                    border: "1px solid #EDE0D4" }}>

        {/* ── LEFT PANEL: frozen Line Item column ─────────────────────── */}
        <div style={{ flexShrink: 0, width: COL_W, zIndex: 2,
                      boxShadow: "3px 0 8px rgba(0,0,0,0.1)" }}>
          <table style={{ borderCollapse: "collapse", width: COL_W, fontSize: 12,
                          tableLayout: "fixed" }}>
            <thead>
              <tr>
                <th style={{ ...thLeft, height: 37 }}>Line Item</th>
              </tr>
              <tr>
                <th style={{ ...sthLeft }}>&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {CATS.map(cat => {
                const catRows = Object.values(byLine).filter(x => x.info.category === cat.key);
                if (!catRows.length) return null;
                return [
                  <tr key={`h-${cat.key}`}>
                    <td style={{ ...catHdrLeft }}>{cat.label}</td>
                  </tr>,
                  ...catRows.map(({ info }) => (
                    <tr key={info.line_item} style={{ background: cat.bg, height: ROW_H }}>
                      <td style={{ padding: "7px 12px", color: B, fontWeight: 600,
                                   fontSize: 12, borderBottom: "1px solid rgba(0,0,0,0.05)",
                                   overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {info.line_item}
                      </td>
                    </tr>
                  )),
                ];
              })}
              {/* Net rows */}
              <tr><td style={{ ...catHdrLeft }}>📊 Net</td></tr>
              <tr style={{ background: "#EBF5E4", height: ROW_H }}>
                <td style={{ padding: "7px 12px", fontWeight: 700, color: B,
                             fontSize: 12, borderBottom: "1px solid rgba(0,0,0,0.05)" }}>
                  Net (Rev − Costs)
                </td>
              </tr>
              <tr style={{ background: "#FFF8E7", height: ROW_H }}>
                <td style={{ padding: "7px 12px", fontWeight: 700, color: B, fontSize: 12 }}>
                  Payroll safe?
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* ── RIGHT PANEL: scrollable months ──────────────────────────── */}
        <div style={{ overflowX: "auto", flex: 1 }}>
          <table style={{ borderCollapse: "collapse", fontSize: 12,
                          tableLayout: "fixed",
                          width: MONTHS.length * CELL_W * 2 + 140 }}>
            <thead>
              <tr>
                {MLABELS.map((m, i) => (
                  <th key={i} colSpan={2}
                    style={{ ...thRight, width: CELL_W * 2 }}>{m}</th>
                ))}
                <th style={{ ...thRight, width: 70, background: "#1A0C05" }}>FY Plan</th>
                <th style={{ ...thRight, width: 70, background: "#1A0C05" }}>FY Actual</th>
              </tr>
              <tr style={{ background: "#F2EDE8" }}>
                {MONTHS.map((_, i) => [
                  <th key={`${i}p`} style={{ ...sthRight, background: "#EBF1F9", color: "#2F5496" }}>Plan ✏️</th>,
                  <th key={`${i}a`} style={{ ...sthRight, color: "#555" }}>Actual</th>,
                ])}
                <th style={sthRight} /><th style={sthRight} />
              </tr>
            </thead>
            <tbody>
              {CATS.map(cat => {
                const catRows = Object.values(byLine).filter(x => x.info.category === cat.key);
                if (!catRows.length) return null;
                return [
                  <tr key={`h-${cat.key}`}>
                    <td colSpan={MONTHS.length * 2 + 2}
                        style={{ background: "#4A2C18", color: "#FAF7F2",
                                 padding: "7px 10px", fontWeight: 700, fontSize: 12 }}>
                      &nbsp;
                    </td>
                  </tr>,
                  ...catRows.map(({ info, months }) => {
                    const fyPlan   = MONTHS.reduce((s, m) => {
                      const pendingVal = pending[months[m]?._id];
                      return s + (pendingVal !== undefined
                        ? (pendingVal === "" ? 0 : Number(pendingVal))
                        : (months[m]?.plan_amount || 0));
                    }, 0);
                    const fyActual = MONTHS.reduce((s, m) => s + (months[m]?.actual_amount || 0), 0);
                    return (
                      <tr key={info.line_item} style={{ background: cat.bg, height: ROW_H }}>
                        {MONTHS.map(m => {
                          const row = months[m];
                          if (!row) return [<td key={`${m}p`} />, <td key={`${m}a`} />];
                          const isPending = row._id in pending;
                          const planVal = isPending ? pending[row._id] : (row.plan_amount ?? "");
                          return [
                            <td key={`${m}p`} style={{ padding: "3px 4px",
                                                       background: isPending ? "#D6E8FF" : "#EBF1F9",
                                                       borderRight: "1px solid #D8E4F0",
                                                       borderBottom: "1px solid rgba(0,0,0,0.04)" }}>
                              <input
                                style={{ ...inp, color: "#2F5496", fontWeight: 600,
                                         background: "transparent",
                                         outline: isPending ? "2px solid #5A9FD4" : "none" }}
                                type="number"
                                value={planVal}
                                onChange={e => stagePlan(row._id, e.target.value)}
                                onWheel={e => e.target.blur()}
                              />
                            </td>,
                            <td key={`${m}a`} style={{ padding: "3px 4px",
                                                       borderRight: "1px solid #E8E0D8",
                                                       borderBottom: "1px solid rgba(0,0,0,0.04)" }}>
                              <input
                                style={inp}
                                type="number"
                                defaultValue={row.actual_amount ?? ""}
                                placeholder="—"
                                onWheel={e => e.target.blur()}
                                onBlur={e => saveActual(row._id, e.target.value)}
                              />
                            </td>,
                          ];
                        })}
                        <td style={{ ...fyCell, fontWeight: 700,
                                     color: fyPlan > 0 ? "#2F5496" : "#aaa" }}>
                          ₹{fyPlan.toLocaleString("en-IN")}
                        </td>
                        <td style={{ ...fyCell, color: "#666" }}>
                          {fyActual > 0 ? `₹${fyActual.toLocaleString("en-IN")}` : "—"}
                        </td>
                      </tr>
                    );
                  }),
                ];
              })}

              {/* Net rows */}
              <tr>
                <td colSpan={MONTHS.length * 2 + 2}
                    style={{ background: B, color: "#FAF7F2",
                             padding: "7px 10px", fontWeight: 700, fontSize: 12 }}>
                  &nbsp;
                </td>
              </tr>
              <tr style={{ background: "#EBF5E4", height: ROW_H }}>
                {nets.map((n, i) => (
                  <td key={`n${i}`} colSpan={2}
                    style={{ textAlign: "center", fontWeight: 700, padding: "8px 4px",
                             color: n.net >= 0 ? "#2E7D32" : "#C00000", fontSize: 12 }}>
                    ₹{n.net.toLocaleString("en-IN")}
                  </td>
                ))}
                <td /><td />
              </tr>
              <tr style={{ background: "#FFF8E7", height: ROW_H }}>
                {nets.map((n, i) => (
                  <td key={`py${i}`} colSpan={2}
                    style={{ textAlign: "center", fontWeight: 700, padding: "8px 4px",
                             fontSize: 12, color: n.rev >= 24000 ? "#2E7D32" : "#C00000" }}>
                    {n.rev >= 24000 ? "✅ YES" : "🔴 NO"}
                  </td>
                ))}
                <td /><td />
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Floating save bar when pending changes exist */}
      {hasPending && (
        <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
                      background: "#1A2E4A", color: "#fff", padding: "12px 24px",
                      borderRadius: 50, boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
                      display: "flex", alignItems: "center", gap: 14, zIndex: 999 }}>
          <span style={{ fontSize: 13 }}>
            ✏️ {Object.keys(pending).length} unsaved change{Object.keys(pending).length > 1 ? "s" : ""}
          </span>
          <button onClick={cancelPlan} style={{ ...btnCancel, fontSize: 12 }}>Discard</button>
          <button onClick={commitPlan} disabled={saving}
            style={{ ...btnSave, fontSize: 12, padding: "6px 18px" }}>
            {saving ? "Saving…" : "✓ Save"}
          </button>
        </div>
      )}
    </div>
  );
}

const ROW_H = 34;

const thLeft   = { background: "#2C1A0E", color: "#FAF7F2", padding: "9px 12px",
                   textAlign: "left", fontWeight: 600, fontSize: 12,
                   borderBottom: "1px solid rgba(255,255,255,0.08)" };
const sthLeft  = { background: "#F2EDE8", padding: "5px 12px", fontWeight: 600,
                   fontSize: 11, borderBottom: "2px solid #DDD4C8", textAlign: "left" };
const catHdrLeft = { background: "#4A2C18", color: "#FAF7F2", padding: "7px 12px",
                     fontWeight: 700, fontSize: 12 };
const thRight  = { background: "#2C1A0E", color: "#FAF7F2", padding: "9px 7px",
                   textAlign: "center", fontWeight: 600, fontSize: 12,
                   borderRight: "1px solid rgba(255,255,255,0.08)" };
const sthRight = { padding: "5px 6px", textAlign: "center", fontWeight: 600,
                   fontSize: 11, borderBottom: "2px solid #DDD4C8",
                   borderRight: "1px solid #EDE0D4" };
const fyCell   = { padding: "7px 8px", textAlign: "right", fontSize: 12,
                   borderLeft: "2px solid #DDD4C8", whiteSpace: "nowrap" };
const inp = {
  width: "100%", padding: "4px 5px",
  border: "1px solid #DDD4C8", borderRadius: 4,
  fontSize: 12, textAlign: "right",
  background: "#fafafa",
  fontFamily: "Inter, sans-serif",
  // Hide spin arrows
  MozAppearance: "textfield",
};
const btnSave   = { padding: "7px 20px", background: "#2E7D32", color: "#fff",
                    border: "none", borderRadius: 6, fontSize: 13,
                    fontWeight: 600, cursor: "pointer" };
const btnCancel = { padding: "7px 16px", background: "transparent", color: "#555",
                    border: "1px solid #ccc", borderRadius: 6, fontSize: 13, cursor: "pointer" };
